import React, {useState, useEffect, useRef, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import {Form, Button} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {
  CardData,
  ExtendedFormWalletCardProps,
  FormConnectSettingsData,
  ShareMethod,
  formSettingToAPI,
} from '../type'
import useWalletCard from '../../../../hooks/useWalletCard'
import {useWalletCardTemplate} from '../../wallet-card-templates/hooks/useWalletCardTemplate'
import {
  MAX_LENGTH,
  SHARE_METHOD,
  S3_WALLET_CARD_PREVIEW_NAME,
} from '../../../../Utils/Constant/WalletCard'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {useDispatch, useSelector} from 'react-redux'
import {startLoadding, stopLoading} from '../../../../Redux/FrontEnd/Loading/LoadingSlice'
import * as Yup from 'yup'
import {contactRegionRegex} from '../../../../Utils/_helpers'
import {getDefaultRegion} from '../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import './FormWalletCard.scss'
import CardInformation from './CardInformation/CardInformation'
import ShareOptions from './ShareOptions/ShareOptions'
import GettingStarted from './GettingStarted/GettingStarted'
import {STEP_KEYS} from '../../../../Utils/Constant/WalletCard'
import * as htmlToImage from 'html-to-image'
import {uploadCanvasToS3} from '../../../../../services/s3Upload'
import {PreviewWalletCardContact} from './PreviewWalletCard/PreviewWalletCardContact/PreviewWalletCardContact'
import SettingFormConnect from './SettingFormConnect/SettingFormConnect'
import {SENDER_DEFAULT, SENDER_TYPE_DEFAULT} from '../../Flow/WorkFlow/FlowCanvas/hooks/useSenderId'
const FormWalletCard: React.FC<ExtendedFormWalletCardProps> = ({
  initialData,
  isEdit = false,
  isTemplate = false,
  onDataChange,
  setCompletedSteps,
  expandedStep = STEP_KEYS.GETTING_STARTED,
  onStepExpand,
  promoSettingIds,
  setPromoSettingIds,
  vehicleTypeOptionIds,
  setVehicleTypeOptionIds,
  formFields,
  setFormFields,
  promoActionList,
  setWalletCardTemplateID,
  walletCardTemplateID,
  templateOptions,
  visitedSteps,
  viewType,
}) => {
  const navigate = useNavigate()
  const {t} = useI18n()
  const dispatch = useDispatch()
  const isLoading = useSelector((state: any) => state.loading.isLoading)
  // Use wallet card custom hook
  const {
    createWalletCard,
    updateWalletCard,
    saveWalletCardDraft,
    updateWalletCardDraft,
    isDraftCreating,
    isDraftUpdating,
  } = useWalletCard()

  // Hook for wallet card template operations
  const {
    createWalletCardTemplate,
    updateWalletCardTemplate,
    updateWalletCardTemplateDraft,
    createWalletCardTemplateDraft,
  } = useWalletCardTemplate()
  const userRegion = useSelector(getDefaultRegion)

  // State for card information with default values
  const [cardData, setCardData] = useState<CardData>(initialData as CardData)

  // State for validation errors
  const [errors, setErrors] = useState<Record<string, string>>({})

  // State for tracking touched fields
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // Individual state for each image type
  const [isLogoUploading] = useState(false)
  const [isBackgroundUploading] = useState(false)
  const [isProfileUploading] = useState(false)

  // Check if any image is currently uploading
  const isAnyImageUploading = () => {
    return isLogoUploading || isBackgroundUploading || isProfileUploading
  }

  // Separate states for each step's completion
  const [gettingStartedCompleted, setGettingStartedCompleted] = useState(false)
  const [cardDetailsCompleted, setCardDetailsCompleted] = useState(false)
  const [shareOptionsCompleted, setShareOptionsCompleted] = useState(false)
  const [formConnectCompleted, setFormConnectCompleted] = useState(false)

  // Avoid updating parent WalletCard on first render to prevent setState-in-render warning
  const isFirstRenderRef = React.useRef(true)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }
    setCompletedSteps?.({
      gettingStarted: gettingStartedCompleted,
      cardDetails: cardDetailsCompleted,
      shareContactDetails: shareOptionsCompleted,
      formConnect: formConnectCompleted,
    })
  }, [
    gettingStartedCompleted,
    cardDetailsCompleted,
    shareOptionsCompleted,
    formConnectCompleted,
    setCompletedSteps,
  ])

  // Update data from props
  useEffect(() => {
    if (initialData) {
      setCardData({
        ...initialData,
        shareMethod: initialData.shareMethod || [ShareMethod.WALLET],
      })
    }
  }, [initialData])

  // Guard: when AI mode is enabled, ensure template is cleared to avoid confusion
  useEffect(() => {
    if (cardData?.aiWelcomeMessage && cardData?.template) {
      setCardData((prev) => ({...prev, template: null}))
    }
  }, [cardData.aiWelcomeMessage])

  // Update user ID when userData changes

  // Yup schema for email validation
  const emailSchema = Yup.string().email('Wrong email format').required('Email is required')

  // Validate email format
  const isValidEmail = (email: string) => {
    try {
      emailSchema.validateSync(email)
      return true
    } catch {
      return false
    }
  }

  // Validate phone number
  const isValidPhone = (phone: string) => {
    const trimmedPhone = String(phone).trim()
    if (trimmedPhone === '') {
      return false
    }
    // Always create a new regex for AU to avoid 'g' flag bug
    const auPattern = contactRegionRegex[userRegion ? userRegion.toUpperCase() : 'AU'].source
    const regex = new RegExp(auPattern, 'm')
    return regex.test(trimmedPhone)
  }

  // Validate website URL
  const isValidWebsite = (website: string) => {
    if (!website) return true // Allow empty website

    // Regex pattern for website validation
    const websitePattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/

    // Test the website against the pattern
    return websitePattern.test(website)
  }

  // Validate a field
  const validateField = (name: string, value: string) => {
    let error = ''
    const trimmedValue = String(value).trim()
    // Check if the field is required
    const requiredFields = isTemplate
      ? ['cardName', 'fullName', 'cardLabel', 'organizationName']
      : ['cardName', 'fullName', 'phone', 'email', 'organizationName', 'cardLabel']
    if (!trimmedValue && requiredFields.includes(name)) {
      error = t('WALLET_CARD.VALIDATION.REQUIRED', {
        name: name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1'),
      })
    }
    if (typeof value === 'string' && value.length > MAX_LENGTH) {
      error = t('WALLET_CARD.VALIDATION.MAX_LENGTH', {
        name: name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1'),
        maxLength: MAX_LENGTH,
      })
    }

    if (name === 'email' && value && !isValidEmail(value)) {
      error = t('WALLET_CARD.VALIDATION.INVALID_EMAIL')
    }
    if (name === 'phone' && value && !isValidPhone(value)) {
      error = t('WALLET_CARD.VALIDATION.INVALID_PHONE')
    }
    if (name === 'shareMethod' && (!value || value.length === 0)) {
      error = t('WALLET_CARD.VALIDATION.REQUIRED_SHARE_METHOD')
    }
    if (name === 'website' && value && !isValidWebsite(value)) {
      error = t('WALLET_CARD.VALIDATION.INVALID_WEBSITE')
    }
    return error
  }

  // Handle field blur for validation
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const {name} = e.target

    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }))

    // Validate the field
    const error = validateField(name, cardData[name as keyof CardData] as string)

    // Update errors state
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }))
  }

  const handleFormConnectDataChange = useCallback(
    (data: FormConnectSettingsData) => {
      setCardData((prevData) => {
        // Only update if formSettings has changed
        if (JSON.stringify(prevData.formSettings) !== JSON.stringify(data.formSettings)) {
          const newData = {
            ...prevData,
            formSettings: data.formSettings.map((setting) => ({
              ...setting,
              _id: setting._id || setting.key,
            })),
          }

          // Also update parent component with the new data
          if (onDataChange) {
            onDataChange({formSettings: data.formSettings})
          }

          return newData
        }
        return prevData // No change
      })
    },
    [onDataChange]
  )

  // Sync formFields changes with cardData.formSettings
  useEffect(() => {
    if (formFields && setFormFields) {
      setCardData((prevData) => {
        // Only update if formSettings has changed
        if (JSON.stringify(prevData.formSettings) !== JSON.stringify(formFields)) {
          const newData = {
            ...prevData,
            formSettings: formFields,
          }

          // Also update parent component with the new data
          if (onDataChange) {
            onDataChange({formSettings: formFields})
          }

          return newData
        }
        return prevData
      })
    }
  }, [formFields, setFormFields, onDataChange])

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value, type, checked} = e.target
    const updatedValue = type === 'checkbox' ? checked : value

    let updatedShareMethod = [...cardData.shareMethod]

    if (name === SHARE_METHOD[0].key || name === SHARE_METHOD[1].key) {
      const methodType = name === SHARE_METHOD[0].key ? ShareMethod.WALLET : ShareMethod.CONTACT

      if (checked) {
        !updatedShareMethod.includes(methodType) && updatedShareMethod.push(methodType)
      } else {
        updatedShareMethod = updatedShareMethod.filter((method) => method !== methodType)
      }
    }
    const updatedData = {
      ...cardData,
      [name]: name === 'backgroundColor' ? value : updatedValue,
      ...(name === SHARE_METHOD[0].key || name === SHARE_METHOD[1].key
        ? {shareMethod: updatedShareMethod}
        : {}),
    }

    setCardData(updatedData)

    // If field has been touched, validate it
    if (touched[name]) {
      const error = validateField(name, updatedValue as string)
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }))
    }

    // Send updated data to parent component
    if (onDataChange) {
      onDataChange(
        name === SHARE_METHOD[0].key || name === SHARE_METHOD[1].key
          ? {shareMethod: updatedShareMethod}
          : {[name]: updatedValue}
      )
    }
  }

  // Validate all fields
  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    const fields: (keyof CardData)[] = [
      'cardName',
      'organizationName',
      'cardLabel',
      'fullName',
      'phone',
      'email',
      'shareMethod',
      'website',
    ]

    // Validate each field
    fields.forEach((field) => {
      const error = validateField(field, cardData[field] as string)
      if (error) {
        newErrors[field] = error
      }
    })

    return newErrors
  }

  const previewCardRef = useRef<HTMLDivElement>(null)

  /**
   * Wait until all <img> inside preview container are fully loaded to avoid missing images
   */
  const waitForImagesLoad = async (container: HTMLElement) => {
    const imgElements = Array.from(container.querySelectorAll('img')) as HTMLImageElement[]
    const unloaded = imgElements.filter((img) => !img.complete)
    if (unloaded.length === 0) return

    await Promise.race([
      new Promise<void>((resolve) => {
        const timer = setTimeout(() => resolve(), 4000) // max 4s wait
        unloaded.forEach((img) => {
          img.addEventListener('load', () => {
            if (unloaded.every((i) => i.complete)) {
              clearTimeout(timer)
              resolve()
            }
          })
          img.addEventListener('error', () => {
            if (unloaded.every((i) => i.complete)) {
              clearTimeout(timer)
              resolve()
            }
          })
        })
      }),
    ])
  }

  /**
   * Convert external image URLs to dataURI within the preview node to bypass CORS when rendering to canvas.
   */
  const inlineExternalImages = async (container: HTMLElement) => {
    const urlToData: Record<string, string> = {}

    const convertUrl = async (url: string): Promise<string> => {
      if (urlToData[url]) return urlToData[url]
      try {
        const resp = await fetch(url, {mode: 'cors'})
        const blob = await resp.blob()
        const reader = new FileReader()
        const base64: string = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result as string)
          reader.readAsDataURL(blob)
        })
        urlToData[url] = base64
        return base64
      } catch (_) {
        return url // fallback to original
      }
    }

    // Process <img>
    const images = Array.from(container.querySelectorAll('img')) as HTMLImageElement[]
    await Promise.all(
      images.map(async (img) => {
        if (img.src && !img.src.startsWith('data:')) {
          img.crossOrigin = 'anonymous'
          const dataUrl = await convertUrl(img.src)
          img.setAttribute('data-original-src', img.src)
          img.src = dataUrl
        }
      })
    )

    // Process elements with inline background-image
    const elements = Array.from(container.querySelectorAll('*')) as HTMLElement[]
    await Promise.all(
      elements.map(async (el) => {
        const bg = getComputedStyle(el).backgroundImage
        if (bg && bg.includes('url(') && !bg.includes('data:')) {
          const match = /url\(["']?(.*?)["']?\)/.exec(bg)
          if (match && match[1]) {
            const url = match[1]
            const dataUrl = await convertUrl(url)
            el.setAttribute('data-original-bg', url)
            el.style.backgroundImage = `url('${dataUrl}')`
          }
        }
      })
    )

    return () => {
      // restore original sources
      images.forEach((img) => {
        const original = img.getAttribute('data-original-src')
        if (original) img.src = original
      })
      elements.forEach((el) => {
        const originalBg = el.getAttribute('data-original-bg')
        if (originalBg) el.style.backgroundImage = `url('${originalBg}')`
      })
    }
  }

  const captureCardAsImage = async () => {
    if (!previewCardRef.current) return null

    try {
      await waitForImagesLoad(previewCardRef.current)

      // Inline external images to data URI
      const restore = await inlineExternalImages(previewCardRef.current)

      const dataUrl = await htmlToImage.toPng(previewCardRef.current, {
        quality: 1.0,
        pixelRatio: 2,
        cacheBust: true,
        skipFonts: true,
        filter: (node) => {
          if (node.tagName === 'LINK' && node.getAttribute('rel') === 'stylesheet') {
            const href = node.getAttribute('href') || ''
            return href.startsWith('/') || href.startsWith('./') || href.startsWith('../')
          }
          return true
        },
      })

      // restore original sources
      restore?.()
      return dataUrl
    } catch (error) {
      console.error('Error capturing card image:', error)
      return null
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Mark all fields as touched to show all errors
    const allFields = Object.keys(cardData)
    allFields.forEach((field) => {
      setTouched((prev) => ({
        ...prev,
        [field]: true,
      }))
    })

    // Validate all fields
    const formErrors = validateForm()
    setErrors(formErrors)

    if (Object.keys(formErrors).length > 0) {
      // Focus on the first field with an error
      const firstErrorField = Object.keys(formErrors)[0]
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement
      if (errorElement) {
        errorElement.focus()
      }
      return
    }

    try {
      dispatch(startLoadding())

      // Capture preview card as image
      const cardImage = await captureCardAsImage()
      let imagePreviewUrl = ''

      if (cardImage) {
        try {
          // Convert base64 to blob
          const response = await fetch(cardImage)
          const blob = await response.blob()

          // Upload to S3
          const timestamp = new Date().getTime()
          const uniqueFileName = `wallet_card_preview_${cardData.user}_${timestamp}.png`
          imagePreviewUrl = await uploadCanvasToS3(
            blob,
            S3_WALLET_CARD_PREVIEW_NAME,
            uniqueFileName,
            t
          )
        } catch (error) {
          console.error('Error uploading preview image:', error)
          toast.error(t('WALLET_CARD.PREVIEW.UPLOAD_ERROR'))
          return
        }
      }

      const submittedData = {
        type: cardData.type,
        user: cardData.user,
        logo: cardData.logo,
        logoGoogle: cardData.logoGoogle,
        picture: cardData.picture,
        pictureGoogle: cardData.pictureGoogle,
        backgroundImage: cardData.backgroundImage,
        backgroundColor: cardData.backgroundColor,
        foregroundColor: cardData.foregroundColor,
        labelColor: cardData.labelColor,
        promoSettings: promoSettingIds,
        cardName: cardData.cardName,
        organizationName: cardData.organizationName,
        cardLabel: cardData.cardLabel,
        fullName: cardData.fullName,
        isDefault: cardData.isDefault || false,
        phone: cardData.phone,
        email: cardData.email,
        shareMethod: cardData.shareMethod,
        imagePreviewUrl: imagePreviewUrl,
        website: cardData.website,
        address: cardData.address,
        jobTitle: cardData.jobTitle,
        companyName: cardData.companyName,
        formSettings: formSettingToAPI(cardData.formSettings),
        formBackgroundColor: cardData.formBackgroundColor,
        formLabelColor: cardData.formLabelColor,
        contactIconColor: cardData.contactIconColor,
        template: cardData.aiWelcomeMessage ? null : cardData.template || null,
        aiWelcomeMessage: cardData.aiWelcomeMessage || false,
        sender: cardData.sender || SENDER_DEFAULT,
        senderType: cardData.senderType || SENDER_TYPE_DEFAULT,
        delayMinutes: cardData.delayMinutes || 0,
        vehicleTypeOptions: vehicleTypeOptionIds ?? [],
      }

      let cardId = ''
      if (isEdit && updateWalletCard) {
        cardId = isTemplate
          ? await updateWalletCardTemplate({...submittedData, id: cardData.id} as any)
          : await updateWalletCard({
              ...submittedData,
              id: cardData.id,
              walletCardTemplate: walletCardTemplateID,
            } as any)
      } else if (createWalletCard) {
        cardId = isTemplate
          ? await createWalletCardTemplate(submittedData as any)
          : await createWalletCard({
              ...submittedData,
              walletCardTemplate: walletCardTemplateID,
            } as any)
      }

      // Update completed steps before navigating
      if (setCompletedSteps) {
        setCompletedSteps({
          gettingStarted: true,
          cardDetails: true,
          shareContactDetails: true,
          formConnect: true,
          share: true,
        })
      }

      // Navigate to success page
      const navigatePath = isTemplate ? '/card-templates/detail' : '/wallet-card/detail'
      navigate(`${navigatePath}/${cardId}`)
    } catch (error: any) {
      toast.error(error?.message || 'Something went wrong!')
    } finally {
      dispatch(stopLoading())
    }
  }

  // Handle save draft
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      dispatch(startLoadding())

      // Capture preview card as image
      const cardImage = await captureCardAsImage()
      let imagePreviewUrl = ''

      if (cardImage) {
        try {
          // Convert base64 to blob
          const response = await fetch(cardImage)
          const blob = await response.blob()

          // Upload to S3
          const timestamp = new Date().getTime()
          const uniqueFileName = `wallet_card_preview_${cardData.user}_${timestamp}.png`
          imagePreviewUrl = await uploadCanvasToS3(
            blob,
            S3_WALLET_CARD_PREVIEW_NAME,
            uniqueFileName,
            t
          )
        } catch (error) {
          toast.error(t('WALLET_CARD.PREVIEW.UPLOAD_ERROR'))
          return
        }
      }

      const submittedData = {
        type: cardData.type,
        user: cardData.user,
        logo: cardData.logo,
        logoGoogle: cardData.logoGoogle,
        picture: cardData.picture,
        pictureGoogle: cardData.pictureGoogle,
        backgroundImage: cardData.backgroundImage,
        backgroundColor: cardData.backgroundColor,
        foregroundColor: cardData.foregroundColor,
        labelColor: cardData.labelColor,
        promoSettings: promoSettingIds,
        cardName: cardData.cardName,
        organizationName: cardData.organizationName,
        cardLabel: cardData.cardLabel,
        fullName: cardData.fullName,
        phone: cardData.phone,
        email: cardData.email,
        shareMethod: cardData.shareMethod,
        imagePreviewUrl: imagePreviewUrl,
        isDefault: cardData.isDefault || false,
        website: cardData.website,
        address: cardData.address,
        jobTitle: cardData.jobTitle,
        companyName: cardData.companyName,
        formSettings: formSettingToAPI(cardData.formSettings),
        formBackgroundColor: cardData.formBackgroundColor,
        formLabelColor: cardData.formLabelColor,
        contactIconColor: cardData.contactIconColor,
        template: cardData.aiWelcomeMessage ? null : cardData.template || null,
        aiWelcomeMessage: cardData.aiWelcomeMessage || false,
        sender: cardData.sender || SENDER_DEFAULT,
        senderType: cardData.senderType || SENDER_TYPE_DEFAULT,
        vehicleTypeOptions: vehicleTypeOptionIds ?? [],
      }

      let cardId = ''
      if (isEdit && updateWalletCardDraft) {
        cardId = isTemplate
          ? await updateWalletCardTemplateDraft({...submittedData, id: cardData.id} as any)
          : await updateWalletCardDraft({
              ...submittedData,
              id: cardData.id,
              walletCardTemplate: walletCardTemplateID,
            })
      } else if (saveWalletCardDraft) {
        cardId = isTemplate
          ? await createWalletCardTemplateDraft(submittedData as any)
          : await saveWalletCardDraft({...submittedData, walletCardTemplate: walletCardTemplateID})
      }
      const navigatePath = isTemplate ? '/card-templates' : '/wallet-card'
      navigate(`${navigatePath}`)
    } catch (error: any) {
      toast.error(error?.message || 'Something went wrong!')
    } finally {
      dispatch(stopLoading())
    }
  }

  return (
    <div className='form-wallet-card'>
      {/* Hidden preview card for capturing */}
      <div className='hidden-preview-card'>
        <div ref={previewCardRef} className='preview-card-container'>
          <PreviewWalletCardContact cardData={cardData} />
        </div>
      </div>

      <Form onSubmit={handleSubmit} noValidate>
        <GettingStarted
          isTemplate={isTemplate}
          cardData={cardData}
          handleChange={handleChange}
          handleBlur={handleBlur}
          touched={touched}
          errors={errors}
          MAX_LENGTH={MAX_LENGTH}
          disableInput={isLoading}
          isCompleted={gettingStartedCompleted}
          setIsCompleted={setGettingStartedCompleted}
          isExpanded={expandedStep === STEP_KEYS.GETTING_STARTED}
          onExpand={() => onStepExpand?.(STEP_KEYS.GETTING_STARTED)}
          setWalletCardTemplateID={setWalletCardTemplateID}
          walletCardTemplateID={walletCardTemplateID}
          templateOptions={templateOptions}
        />

        <CardInformation
          isTemplate={isTemplate}
          cardData={cardData}
          handleChange={handleChange}
          handleBlur={handleBlur}
          touched={touched}
          errors={errors}
          MAX_LENGTH={MAX_LENGTH}
          disableInput={isLoading}
          isCompleted={cardDetailsCompleted}
          setIsCompleted={setCardDetailsCompleted}
          isExpanded={expandedStep === STEP_KEYS.CARD_DETAILS}
          onExpand={() => onStepExpand?.(STEP_KEYS.CARD_DETAILS)}
          viewType={viewType}
        />

        <div className={`${isTemplate ? '' : 'd-none'}`}>
          <ShareOptions
            cardData={cardData}
            handleChange={handleChange}
            handleBlur={handleBlur}
            touched={touched}
            errors={errors}
            isLoading={isLoading}
            isCompleted={shareOptionsCompleted}
            setIsCompleted={setShareOptionsCompleted}
            isExpanded={expandedStep === STEP_KEYS.SHARE_CONTACT_DETAILS}
            onExpand={() => onStepExpand?.(STEP_KEYS.SHARE_CONTACT_DETAILS)}
            gateUntilOpened={isTemplate && !isEdit}
          />
        </div>

        <div className={`${isTemplate ? '' : 'd-none'}`}>
          <SettingFormConnect
            cardData={cardData}
            handleChange={handleChange}
            handleBlur={handleBlur}
            touched={touched}
            errors={errors}
            MAX_LENGTH={MAX_LENGTH}
            disableInput={isLoading}
            isCompleted={formConnectCompleted}
            setIsCompleted={setFormConnectCompleted}
            isExpanded={expandedStep === STEP_KEYS.FORM_CONNECT}
            onExpand={() => onStepExpand?.(STEP_KEYS.FORM_CONNECT)}
            isLoading={isLoading}
            promoSettingIds={promoSettingIds}
            setPromoSettingIds={setPromoSettingIds}
            vehicleTypeOptionIds={vehicleTypeOptionIds}
            setVehicleTypeOptionIds={setVehicleTypeOptionIds}
            formFields={formFields}
            setFormFields={setFormFields}
            promoActionList={promoActionList}
            onFormConnectDataChange={handleFormConnectDataChange}
            gateUntilOpened={isTemplate && !isEdit}
          />
        </div>

        <div className='d-flex justify-content-end mt-4 gap-2'>
          {cardData?.status !== 'active' && (
            <Button
              variant='outline-primary'
              className='btn btn-outline-primary h-40px btn-save-draft'
              onClick={handleSave}
              disabled={isDraftCreating || isDraftUpdating || isAnyImageUploading()}
            >
              {t('WALLET_CARD.FORM.SHARE_OPTIONS.ACTIONS.SAVE_DRAFT')}
            </Button>
          )}

          <Button
            variant='primary'
            onClick={handleSubmit}
            className='btn btn-primary h-40px'
            disabled={
              isLoading ||
              isAnyImageUploading() ||
              !shareOptionsCompleted ||
              !cardDetailsCompleted ||
              !gettingStartedCompleted ||
              !formConnectCompleted ||
              (isTemplate &&
                !isEdit &&
                !(visitedSteps?.shareContactDetails && visitedSteps?.formConnect))
            }
          >
            {isTemplate
              ? t('MOBILE_WALLET_CARD.FORM.SHARE_OPTIONS.ACTIONS.SAVE_TEMPLATE')
              : t('WALLET_CARD.FORM.SHARE_OPTIONS.ACTIONS.CARD')}
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default FormWalletCard
